import { reaction, toJS } from "mobx";
import UserStore from "./User.store";
import GroupsStore, { localizeNoGroup } from "./Groups.store";
import DevicesStore from "./Devices.store";
import MapStore from "./Map.store";
import TelemetryStore from "./Telemetry.store";
import LanguageStore from "./Language.store";
import DownloadStore from "./Download.store";
import AppStore, { windows } from "./App.store";
import SidebarStore from "./Sidebar.store";
import SubusersStore from "./Subusers.store";
import SupportStore from "./Support.store";
import ReportsStore from "./Reports.store";
import ModalStore from "./Modal.store";
import moment from "moment";
import GeoZoneStore from "./Geozone.store";

export class RootStore {
    user: UserStore;

    groups: GroupsStore;
    devices: DevicesStore;
    map: MapStore;
    telemetry: TelemetryStore;
    lang: LanguageStore;
    download: DownloadStore;
    app: AppStore;
    sidebar: SidebarStore;
    subusers: SubusersStore;
    support: SupportStore;
    reports: ReportsStore;
    modal: ModalStore;
    geozones: GeoZoneStore;

    constructor() {
        this.user = new UserStore(this);

        this.groups = new GroupsStore(this);
        this.devices = new DevicesStore(this);
        this.map = new MapStore(this);
        this.telemetry = new TelemetryStore(this);
        this.lang = new LanguageStore(this);
        this.download = new DownloadStore(this);
        this.app = new AppStore(this);
        this.sidebar = new SidebarStore(this);
        this.subusers = new SubusersStore(this);
        this.support = new SupportStore(this);
        this.reports = new ReportsStore(this);
        this.modal = new ModalStore(this);
        this.geozones = new GeoZoneStore(this);

        (window as any).rootStore = () => ({
            app: toJS(this.app),
            devices: toJS(this.devices),
        });
    }

    setDefault() {
        this.user.setDefault();
        this.groups.setDefault();
        this.devices.setDefault();
        this.map.setDefault();
        this.telemetry.setDefault();
        this.lang.setDefault();
        this.download.setDefault();
        this.app.setDefault();
        this.sidebar.setDefault();
        this.subusers.setDefault();
        this.modal.setDefault();
    }
}

export const rootStore = new RootStore();

reaction(
    () => rootStore.telemetry.limit,
    (limit) => {
        localStorage.setItem("deviceTelemetyLimit", `${limit}`);
    }
);
reaction(
    () => rootStore.telemetry.columnVisibilityModel,
    (data) => {
        const { ...columnVisibilityModel } = data;
        const value = JSON.stringify(columnVisibilityModel);
        localStorage.setItem("columnVisibilityModel", value);
    }
);
reaction(
    () => rootStore.lang.lang,
    () => {
        rootStore.map.setReload();
        rootStore.groups.groupsList = localizeNoGroup(
            rootStore.groups.groupsList,
            rootStore.lang
        );
    }
);
reaction(
    () => rootStore.app.deviceSettingsVisible,
    (deviceSettingsVisible) => {
        if (!deviceSettingsVisible) {
            rootStore.devices.resetSettingDeviceImei();
        } else {
            rootStore.telemetry.resetTelemetryVisible();
        }
    }
);
reaction(
    () => rootStore.app.deviceEditVisible,
    (deviceEditVisible) => {
        if (!deviceEditVisible) {
            rootStore.devices.resetEditedDeviceImei();
        }
    }
);
reaction(
    () => rootStore.app.deviceInfoVisible,
    (deviceInfoVisible) => {
        if (!deviceInfoVisible) {
            rootStore.devices.resetInfoDeviceImei();
        }
    }
);
reaction(
    () => rootStore.app.page,
    (page) => {
        console.log(page);
        rootStore.devices.getGroupsDevices();
        rootStore.app.closeDeviceSettings();
        rootStore.app.closeDeviceEdit();
        rootStore.app.closeDeviceInfo();
        rootStore.map.resetHighlightedCoords();
        rootStore.map.resetHighlightedDevice();
        rootStore.map.resetSelectedDevices();
        rootStore.map.resetHistoryCoordsList();
        rootStore.telemetry.resetPage();
        rootStore.telemetry.resetSelectedDevice();
        rootStore.telemetry.resetCurrentTelemetry();
        rootStore.map.resetSelectedGroupId();
        rootStore.map.setBounds();

        if (page === "support") {
            rootStore.devices.getAllDevices();
        }

        if (page === "history" || page === "support") {
            let imei =
                rootStore.map.selectedDevice ??
                localStorage.getItem("selectedDevice");
            if (!imei) {
                const [device] = rootStore.devices.deviceList;
                if (device?.imei) imei = device.imei;
            }
            rootStore.map.setSelectedDevice(imei!);
            rootStore.telemetry.setSelectedDevice(imei!);
        } else {
            rootStore.map.resetSelectedDevice();
        }

        if(page === "geozones"){
            rootStore.geozones.fetchGeozones();
        }
    }
);
reaction(
    () => rootStore.map.dateFilter,
    (dateFilter) => {
        const tz = rootStore.user.settings.timezone;
        let from, to;
        to = moment().utc(true).subtract(tz, "m").toDate();
        switch (dateFilter) {
            case "last24Hours":
                from = moment()
                    .utc(true)
                    .subtract(tz, "m")
                    .subtract(24, "h")
                    .toDate();
                rootStore.map.setFromDateFilter(from);
                rootStore.map.setToDateFilter(to);
                break;
            case "last3Days":
                from = moment()
                    .utc(true)
                    .subtract(tz, "m")
                    .subtract(3, "d")
                    .toDate();
                rootStore.map.setFromDateFilter(from);
                rootStore.map.setToDateFilter(to);
                break;
            case "lastWeek":
                from = moment()
                    .utc(true)
                    .subtract(tz, "m")
                    .subtract(7, "d")
                    .toDate();
                rootStore.map.setFromDateFilter(from);
                rootStore.map.setToDateFilter(to);
                break;
        }
        rootStore.telemetry.getTelemetry();
    }
);
reaction(
    () => rootStore.map.highlightedCoords,
    () => {
        rootStore.map.startHighlightTimer();
    }
);
reaction(
    () => rootStore.map.highlightedDevice,
    () => {
        rootStore.map.startHighlightTimer();
    }
);
reaction(
    () => rootStore.app.cookiesApproved,
    (cookiesApproved) => {
        localStorage.setItem("cookiesApproved", `${cookiesApproved}`);
    }
);
reaction(
    () => rootStore.app.demoMode,
    (demoMode) => {
        localStorage.setItem("demoMode", `${demoMode}`);
    }
);
reaction(
    () => rootStore.map.selectedGroupId,
    (selectedGroupId) => {
        if (rootStore.app.page !== "support") {
            rootStore.devices.getGroupLastCoords(selectedGroupId);
            rootStore.map.setSelectedDevices(selectedGroupId);
            rootStore.map.setBounds();
        }

        if (
            selectedGroupId &&
            rootStore.app.page !== "history" &&
            rootStore.app.page !== "support"
        ) {
            localStorage.setItem("groupId", selectedGroupId);
        }
    }
);
reaction(
    () => rootStore.map.selectedDevices,
    (selectedDevices) => {
        const groupId = rootStore.map.selectedGroupId;
        if (selectedDevices.length === 0 && groupId !== null) {
            rootStore.map.resetSelectedGroupId();
        }
    }
);
reaction(
    () => rootStore.telemetry.isTelemetryVisible,
    () => {
        rootStore.map.setReload();
    }
);
reaction(
    () => rootStore.map.selectedDevice,
    (imei) => {
        if (
            imei &&
            rootStore.app.page !== "history" &&
            rootStore.app.page !== "support"
        ) {
            const devices = rootStore.devices.deviceList;
            const [device] = devices.filter((d) => d.imei === imei);
            if (device) {
                const { groupId } = device;
                rootStore.map.setSelectedGroupId(groupId);
                localStorage.setItem("selectedDevice", imei);
            }
        } else if (
            rootStore.app.page === "history" ||
            rootStore.app.page === "support"
        ) {
            if (imei) {
                localStorage.setItem("selectedDevice", imei);
                rootStore.telemetry.setSelectedDevice(imei);

                const [device] = rootStore.map.lastCoordsList.filter(
                    (device) => {
                        return device.imei === imei;
                    }
                );

                if (device) {
                    const { lat, lon } = device.gpsCoord;
                    if (lat && lon) {
                        rootStore.map.setCurrentPosition([lat, lon]);
                    }
                    rootStore.map.setPosition(device.gpsCoord);
                    rootStore.map.setZoom(18);
                }
            } else {
                localStorage.setItem("selectedDevice", "");
                rootStore.telemetry.resetSelectedDevice();
                rootStore.telemetry.resetPage();
                rootStore.telemetry.resetCurrentTelemetry();
                rootStore.map.resetHistoryCoordsList();
            }
        }
    }
);
reaction(
    () => rootStore.telemetry.selectedDevice,
    (selectedDevice) => {
        if (selectedDevice) {
            rootStore.telemetry.getTelemetry();
        }
    }
);
reaction(
    () => rootStore.map.fromDateFilter,
    () => {
        const imei = rootStore.telemetry.selectedDevice;
        if (
            imei &&
            (rootStore.app.page === "history" ||
                rootStore.app.page === "reports" ||
                rootStore.app.page === "support")
        ) {
            rootStore.map.getHistoryCoordsList(imei);
            rootStore.telemetry.setPage(1);
        }
    }
);
reaction(
    () => rootStore.map.toDateFilter,
    () => {
        const imei = rootStore.telemetry.selectedDevice;
        if (
            imei &&
            (rootStore.app.page === "history" ||
                rootStore.app.page === "reports" ||
                rootStore.app.page === "support")
        ) {
            const imei = rootStore.map.selectedDevice;
            imei && rootStore.map.getHistoryCoordsList(imei);
            rootStore.telemetry.setPage(1);
        }
    }
);
reaction(
    () => rootStore.devices.loading,
    (loading) => {
        if (!loading && rootStore.app.page === "subusers") {
            rootStore.subusers.getSubusers();
        }
    }
);
reaction(
    () => rootStore.devices.deviceList,
    (deviceList) => {
        rootStore.map.switchDate(deviceList);
    }
);
reaction(
    () => rootStore.devices.settingDeviceImei,
    (imei) => {
        if(imei){
            rootStore.devices.getDeviceSettings(imei).then(() => {
                rootStore.devices.setSelectedDeviceSettings(imei);
            })
        }
    }
);
reaction(
    () => rootStore.devices.settingsList,
    () => {
        const imei = rootStore.devices.settingDeviceImei;
        if (imei) rootStore.devices.setSelectedDeviceSettings(imei);
    }
);
reaction(
    () => rootStore.devices.lastCoordsList,
    (lastCoordsList) => {
        rootStore.map.setLastCoordsList(lastCoordsList);

        if (rootStore.app.page === "support") {
            const imei = rootStore.map.selectedDevice;
            if (!imei) return;
            const [device] = rootStore.map.lastCoordsList.filter((device) => {
                return device.imei === imei;
            });

            if (device) {
                const { lat, lon } = device.gpsCoord;
                if (lat && lon) {
                    rootStore.map.setCurrentPosition([lat, lon]);
                }
                rootStore.map.setPosition(device.gpsCoord);
                rootStore.map.setZoom(18);
            }
        }
    }
);
reaction(
    () => rootStore.sidebar.isSidebarHidden,
    () => {
        rootStore.map.setReload();
    }
);
reaction(
    () => rootStore.map.historyCoordsList,
    () => {
        rootStore.map.setReload();
    }
);
reaction(
    () => rootStore.devices.deviceList,
    () => {
        rootStore.map.setReload();
        rootStore.devices.getDevicesSettings();
        const selectedGroupId = rootStore.map.selectedGroupId;
        if (selectedGroupId && rootStore.app.page !== "support") {
            rootStore.devices.getGroupLastCoords(selectedGroupId);
        }
        if (
            rootStore.app.page === "history" ||
            rootStore.app.page === "support"
        ) {
            const imei = rootStore.map.selectedDevice;
            if (imei) rootStore.map.getHistoryCoordsList(imei);
        }
    }
);
reaction(
    () => rootStore.subusers.subusersList,
    (subusersList) => {
        const subuser = rootStore.subusers.selectedSubuser;
        if (subuser && subusersList.indexOf(subuser) === -1) {
            rootStore.subusers.resetSelectedSubuser();
        }
    }
);

const telemetryReaction = () => {
    reaction(
        () => rootStore.telemetry.telemetryList,
        () => {
            rootStore.telemetry.setPage(1);
            const imei = rootStore.telemetry.selectedDevice;
            if (imei) {
                rootStore.map.getHistoryCoordsList(imei);
            }
        }
    );
};
telemetryReaction();
const authReaction = () => {
    reaction(
        () => rootStore.user.token,
        (token) => {
            if (!token) {
                rootStore.setDefault();
                rootStore.app.resetDemoMode();
            }
        }
    );
};
authReaction();

const userSettingsReaction = () => {
    reaction(
        () => rootStore.user.settings,
        (settings, prev) => {
            const { timezone } = settings;

            if (prev.timezone !== undefined && prev.timezone !== timezone) {
                rootStore.map.resetDataFilters();
                rootStore.map.setDateFilter("last24Hours");
                if (rootStore.telemetry.selectedDevice) {
                    rootStore.telemetry.getTelemetry();
                }
            }
        }
    );
};
userSettingsReaction();
